import React, { FC, useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { Button, Dialog, Empty, Form, Input, NoticeBar, Popup, Selector, Space, Switch, TextArea, Toast } from 'antd-mobile';
import { DatePicker } from 'antd-mobile/es/components/date-picker/date-picker';
import Webcam from 'react-webcam';

import { useClient } from '../../../hooks/use-client';
import { useNavbar } from '../../../hooks/use-navbar';
import {
  useBlockedClients,
  findBlockedMatch,
  ensureBlockedData,
} from '../../../hooks/use-blocked-clients';
import { InterventionEditJson, InterventionJson, PrestationEnum } from '../../../types/models';
import { FormValues } from '../../../types/forms';
import { LoadingComponent } from '../ui';
import { apiClient } from '../../../api';
import { GetInterventionsResponse } from '../../../api/endpoints/interventions';
import { faroLogger } from '../../../observability/faro-logger';

import { PopupValidation } from './PopupValidation';
import { findExpectedVehicleType, findPrestationCost, findSameDayDuplicate, getChoices } from './interventionUtils';

function useQuery(): URLSearchParams {
  const { search } = useLocation();

  return React.useMemo(() => new URLSearchParams(search), [search]);
}

const NewIntervention: FC = () => {
  const navbar = useNavbar();
  const navigate = useNavigate();
  const query = useQuery();

  const { clientId } = useParams<{ clientId: string }>();
  const { interventionId } = useParams<{ interventionId: string }>();
  // Retour après édition : si on vient du bon (?from=bon&day=…), on y retourne sur le bon jour ;
  // sinon comportement par défaut (fiche client).
  const returnPath =
    query.get('from') === 'bon' ? `/client/${clientId}/bon${query.get('day') ? `?day=${query.get('day')}` : ''}` : undefined;

  const { client, costs, loading: clientLoading } = useClient(clientId);
  const blocked = useBlockedClients();

  const initialIntervention: InterventionEditJson = {
    interior: false,
    exterior: false,
    disinfection: false,
    decontamination: false,
    package: false,
    comment: '',
    driver: '',
    photo_plate: query.get('file') || '',
    immatriculation: query.get('immat') || '',
    parc_number: query.get('parc-number') || '',
    immatriculation_bis: '',
    parc_number_bis: '',
    timestamp: new Date().toISOString(),
    prestation_costs: undefined,
    client_id: 0,
    author_id: undefined,
  };
  const [intervention, setIntervention] = useState<InterventionEditJson>(initialIntervention);

  const [form] = Form.useForm<FormValues>();
  const [warnings, setWarnings] = useState({
    vehicleType: false,
    prestations: false,
    identifier: false,
  });
  const [showComment, setShowComment] = useState(false);
  const [popinVisible, setPopinVisible] = useState(false);
  const [datePickerVisible, setDatePickerVisible] = useState(false);
  const [scanKind, setScanKind] = useState<'immat' | 'parc' | null>(null);
  const [scanBusy, setScanBusy] = useState(false);
  const [scanCameraError, setScanCameraError] = useState<string | null>(null);
  const webcamRef = useRef<Webcam | null>(null);

  /**
   * Historique du client, prefetche des l ouverture du formulaire pour que
   * les pre-checks de validation ne coutent plus d attente au clic. On garde
   * la promesse (pas le resultat) : si l agent valide avant qu elle soit
   * resolue, onSave l attend simplement.
   */
  const historyRef = useRef<Promise<GetInterventionsResponse | null> | null>(null);

  const historyClientId = client?.id;
  useEffect(() => {
    if (!historyClientId) {
      return;
    }
    historyRef.current = apiClient.interventions
      .getInterventions({ filter: `client_id||eq||${historyClientId}` })
      .catch((e) => {
        console.warn('intervention history prefetch failed', e);
        return null;
      });
  }, [historyClientId]);

  useEffect(() => {
    if (!client || clientLoading || !costs || !costs.length) {
      return;
    }
    if (interventionId) {
      faroLogger.info('intervention_form_opened', {
        action: 'edit',
        interventionId: Number(interventionId),
        clientId: client.id,
        clientName: client.name,
      });

      apiClient.interventions
        .getInterventionById(Number(interventionId))
        .then((data) => {
          setIntervention(data);
          form.setFieldsValue({
            ...data,
            vehicle_type: [...[data.prestation_costs?.title || '']],
            prestations: [
              data.interior ? 'interior' : '',
              data.exterior ? 'exterior' : '',
              data.disinfection ? 'disinfection' : '',
              data.decontamination ? 'decontamination' : '',
              data.package ? 'package' : '',
            ],
          });
        })
        .catch((error) => {
          faroLogger.error('intervention_load_failed', {
            interventionId: Number(interventionId),
            error: error?.message || 'Unknown error',
          });
          throw Error(error);
        });
    } else {
      faroLogger.info('intervention_form_opened', {
        action: 'create',
        clientId: client.id,
        clientName: client.name,
        vehicleType: query.get('type'),
        hasPhoto: !!query.get('file'),
      });
    }
    const vehicle_type = findPrestationCost(costs, query.get('type'));
    setIntervention((intervention) => ({ ...intervention, prestation_costs: vehicle_type, client_id: client.id }));
    form.setFieldValue('vehicle_type', [vehicle_type?.title]);
  }, [client, costs, query.get('type'), clientLoading, interventionId]);

  useEffect(() => {
    if (!navbar || !client) {
      return;
    }
    const returnToHome = (): void => {
      navigate(returnPath ?? '/client/' + client.id);
    };

    navbar.setTitle(client.name);
    navbar.setBackArrow(true);
    navbar.setOnBack(returnToHome);
    navbar.setRight();
  }, [client]);

  const dataURLtoBlob = (dataurl: string): Blob => {
    const arr = dataurl.split(',');
    const mime = (arr[0].match(/:(.*?);/) || [])[1] || 'image/png';
    const bstr = atob(arr[1]);
    let n = bstr.length;
    const u8arr = new Uint8Array(n);
    while (n--) {
      u8arr[n] = bstr.charCodeAt(n);
    }
    return new Blob([u8arr], { type: mime });
  };

  const handleScanCapture = async (): Promise<void> => {
    if (!webcamRef.current || !scanKind) {
      return;
    }
    const dataUri = webcamRef.current.getScreenshot();
    if (!dataUri) {
      Toast.show({ content: 'Impossible de capturer', icon: 'fail' });
      return;
    }
    setScanBusy(true);
    faroLogger.info('intervention_scan_started', {
      clientId: client?.id,
      kind: scanKind,
    });
    try {
      const formData = new FormData();
      formData.append('file', dataURLtoBlob(dataUri), 'plate.png');
      formData.append('kind', scanKind === 'immat' ? 'immat' : 'parc-number');
      const result = await fetch(
        `${process.env.REACT_APP_BACKEND_HOST}/api2/plates/upload`,
        {
          method: 'POST',
          headers: { version: '' + process.env.REACT_APP_VERSION },
          body: formData,
        }
      ).then((r) => r.json());

      const first = Array.isArray(result) && result.length > 0 ? result[0] : null;
      const plate = (first?.plate || '').trim();
      if (!plate) {
        Toast.show({ content: 'Rien détecté, réessayez', icon: 'fail' });
        faroLogger.warn('intervention_scan_no_result', { kind: scanKind });
        setScanBusy(false);
        return;
      }
      if (scanKind === 'immat') {
        form.setFieldValue('immatriculation', plate);
      } else {
        form.setFieldValue('parc_number', plate);
      }
      // Rattache la photo à l'intervention : sans ça, le scan intégré remplissait la plaque
      // mais `photo_plate` restait vide → la photo (pourtant stockée) n'apparaissait pas dans l'admin.
      if (first.filename) {
        setIntervention((prev) => ({ ...prev, photo_plate: first.filename as string }));
      }
      setWarnings((w) => ({ ...w, identifier: false }));
      Toast.show({ content: 'Détecté : ' + plate, icon: 'success' });
      faroLogger.info('intervention_scan_success', { kind: scanKind, plate });
      setScanKind(null);
    } catch (err) {
      Toast.show({ content: 'Erreur scan', icon: 'fail' });
      faroLogger.error('intervention_scan_failed', {
        kind: scanKind,
        error: err instanceof Error ? err.message : String(err),
      });
    } finally {
      setScanBusy(false);
    }
  };

  if (!client || clientLoading || !costs || !costs.length) {
    return <LoadingComponent />;
  }

  const initialValues: FormValues = {
    ...intervention,
    vehicle_type: [...[intervention.prestation_costs?.title || '']],
    prestations: [
      intervention.interior ? 'interior' : '',
      intervention.exterior ? 'exterior' : '',
      intervention.disinfection ? 'disinfection' : '',
      intervention.decontamination ? 'decontamination' : '',
      intervention.package ? 'package' : '',
    ],
  };

  //
  const handleVehicleTypeChange = (vehicle_types: string[]): void => {
    const vehicleType = vehicle_types.length ? vehicle_types[0] : undefined;

    faroLogger.info('intervention_vehicle_type_changed', {
      clientId: client?.id,
      vehicleType,
    });

    setIntervention((intervention) => ({
      ...intervention,
      ...{ interior: false, exterior: false, disinfection: false, package: false, decontamination: false },
      prestation_costs: vehicle_types.length ? findPrestationCost(costs, vehicle_types[0]) : undefined,
    }));
    form.setFieldValue('prestations', []);
  };

  const handlePrestationsChange = (prestationsSelected: PrestationEnum[]): void => {
    const hasPackage = prestationsSelected.includes('package');
    const hasIntOrExt = prestationsSelected.includes('interior') || prestationsSelected.includes('exterior');

    if (hasPackage && hasIntOrExt) {
      const cleaned = prestationsSelected.filter((p) => p !== 'interior' && p !== 'exterior');
      form.setFieldValue('prestations', cleaned);
      Dialog.alert({
        title: 'Combinaison invalide',
        content: 'Le "Pack" inclut déjà le nettoyage intérieur ET extérieur. Les options "Int" et "Ext" ont été retirées.',
        confirmText: 'OK',
      });
      prestationsSelected = cleaned;
    }

    faroLogger.info('intervention_prestations_changed', {
      clientId: client?.id,
      prestations: prestationsSelected,
      vehicleType: intervention.prestation_costs?.title,
    });

    setIntervention((intervention) => ({
      ...intervention,
      ...prestationsSelected.reduce<{
        interior: boolean;
        exterior: boolean;
        disinfection: boolean;
        package: boolean;
        decontamination: boolean;
      }>(
        (acc, cur) => {
          acc[cur] = true;
          return acc;
        },
        { interior: false, exterior: false, disinfection: false, package: false, decontamination: false },
      ),
    }));
  };
  const validateForm = (values: FormValues): boolean => {
    setWarnings({
      vehicleType: false,
      prestations: false,
      identifier: false,
    });

    // Check if form valid
    let error = false;
    const validationErrors: string[] = [];

    if (!intervention.prestation_costs) {
      setWarnings((prev) => ({ ...prev, vehicleType: true }));
      validationErrors.push('missing_vehicle_type');
      error = true;
    }
    if (!values.immatriculation.trim().length && !values.parc_number.trim().length) {
      setWarnings((prev) => ({ ...prev, identifier: true }));
      validationErrors.push('missing_identifier');
      error = true;
    }
    if (
      !intervention.decontamination &&
      !intervention.disinfection &&
      !intervention.interior &&
      !intervention.exterior &&
      !intervention.package
    ) {
      setWarnings((prev) => ({ ...prev, prestations: true }));
      validationErrors.push('missing_prestations');
      error = true;
    }

    if (error) {
      faroLogger.warn('intervention_validation_failed', {
        clientId: client?.id,
        errors: validationErrors,
      });
    }

    return error;
  };

  const onSave = async (values: FormValues): Promise<void> => {
    /** Form validation */
    const error = validateForm(values);
    if (error) {
      return;
    }

    /**
     * Pre-check : type de vehicule incoherent avec l historique. On cherche
     * parmi les interventions du client celles qui partagent l immat ou le
     * n° de parc saisi, et on regarde le type le plus frequent. Si different
     * du type selectionne, on alerte l agent avant de poursuivre.
     *
     * L historique est prefetche a l ouverture du formulaire (voir
     * historyRef) : au clic sur Valider il est deja la, l agent n attend
     * plus le reseau. Si le prefetch n a pas encore repondu, on l attend
     * ici — c est le meme aller-retour qu avant, jamais pire.
     */
    if (client && intervention.prestation_costs) {
      try {
        const response = await (historyRef.current ??
          apiClient.interventions.getInterventions({ filter: `client_id||eq||${client.id}` }));
        if (!response) {
          throw new Error('historique indisponible');
        }
        const excludeId = interventionId ? Number(interventionId) : undefined;
        const identifier = values.immatriculation.trim() || values.parc_number.trim();

        // 1. Type de vehicule incoherent avec l historique
        const expectedType = findExpectedVehicleType(
          response.data,
          values.immatriculation,
          values.parc_number,
          excludeId,
        );
        if (expectedType && expectedType !== intervention.prestation_costs.title) {
          const confirmed = await Dialog.confirm({
            title: '⚠ Type de véhicule à vérifier',
            content: `Pour ${identifier}, le type "${expectedType}" est celui enregistré le plus souvent par le passé. Vous avez sélectionné "${intervention.prestation_costs.title}". Vérifiez le type avant de valider.`,
            confirmText: 'Confirmer ce type',
            cancelText: 'Modifier',
          });
          if (!confirmed) {
            faroLogger.info('intervention_vehicle_type_mismatch_cancelled', {
              clientId: client.id,
              identifier,
              selectedType: intervention.prestation_costs.title,
              expectedType,
            });
            return;
          }
          faroLogger.info('intervention_vehicle_type_mismatch_confirmed', {
            clientId: client.id,
            identifier,
            selectedType: intervention.prestation_costs.title,
            expectedType,
          });
        }

        // 2. Doublon le meme jour pour le meme vehicule
        const dup = findSameDayDuplicate(
          response.data,
          values.immatriculation,
          values.parc_number,
          intervention.timestamp,
          excludeId,
        );
        if (dup) {
          const dupTime = (dup.timestamp || '').slice(11, 16).replace(':', 'h');
          const confirmed = await Dialog.confirm({
            title: '⚠ Intervention déjà saisie aujourd’hui',
            content: `Une intervention a déjà été enregistrée pour ${identifier} aujourd’hui à ${dupTime}. C’est bien une nouvelle intervention ?`,
            confirmText: 'Oui, valider',
            cancelText: 'Annuler',
          });
          if (!confirmed) {
            faroLogger.info('intervention_same_day_duplicate_cancelled', {
              clientId: client.id,
              identifier,
              existingId: dup.id,
              existingTime: dupTime,
            });
            return;
          }
          faroLogger.info('intervention_same_day_duplicate_confirmed', {
            clientId: client.id,
            identifier,
            existingId: dup.id,
            existingTime: dupTime,
          });
        }
      } catch (e) {
        // Si la requete echoue, on ne bloque pas la saisie
        console.warn('intervention pre-save check failed', e);
      }
    }

    /**
     * Pre-check : client bloque (factures impayees). Visible uniquement
     * pour les emails whitelistes (gere par useBlockedClients).
     * On force ensureBlockedData() pour eviter la fenetre vide au mount,
     * la popup doit s afficher a CHAQUE saisie sur un client bloque.
     */
    if (client && blocked.enabled) {
      const list = await ensureBlockedData();
      const match = list.length > 0 ? findBlockedMatch(client.name, list) : null;
      faroLogger.info('blocked_check_save', {
        clientId: client.id,
        clientName: client.name,
        listSize: list.length,
        matched: !!match,
        blockedName: match?.name,
      });
      if (match) {
        const totalDue = new Intl.NumberFormat('fr-FR', {
          style: 'currency',
          currency: 'EUR',
          maximumFractionDigits: 2,
        }).format(match.totalDue);
        const confirmed = await Dialog.confirm({
          title: '⚠ Client bloqué',
          content: `${match.invoices.length} facture${
            match.invoices.length > 1 ? 's' : ''
          } impayée${
            match.invoices.length > 1 ? 's' : ''
          } pour ${match.name} · ${totalDue} dûs au total. Valider quand même cette intervention ?`,
          confirmText: 'Valider quand même',
          cancelText: 'Annuler',
        });
        if (!confirmed) {
          faroLogger.warn('intervention_blocked_client_cancelled', {
            clientId: client.id,
            clientName: client.name,
            blockedName: match.name,
            totalDue: match.totalDue,
          });
          return;
        }
        faroLogger.warn('intervention_blocked_client_confirmed', {
          clientId: client.id,
          clientName: client.name,
          blockedName: match.name,
          totalDue: match.totalDue,
        });
      }
    }

    /**
     * author_id: si existe deja, on le garde pour pas reatribuer à un autre agent en cas de modif
     * timestamp pareil, on peut pas le changer en front
     * id quand c'est du patch
     */
    setIntervention((intervention) => ({
      ...intervention,
      comment: form.getFieldValue('comment'),
      driver: form.getFieldValue('driver'),
      immatriculation: form.getFieldValue('immatriculation'),
      parc_number: form.getFieldValue('parc_number'),
      immatriculation_bis: client.has_additional_vehicles ? form.getFieldValue('immatriculation_bis') : undefined,
      parc_number_bis: client.has_additional_vehicles ? form.getFieldValue('parc_number_bis') : undefined,
    }));

    setPopinVisible(true);
  };

  const choices = getChoices(intervention.prestation_costs);

  return (
    <div className="new-intervention-panel">
      <Form
        initialValues={initialValues}
        form={form}
        style={{ paddingBottom: 55 }}
        layout="horizontal"
        mode="card"
        onFinish={onSave}
        footer={
          <Button block type="submit" color="primary" size="large">
            Enregistrer
          </Button>
        }
      >
        <Form.Header>Date de l'inter. :</Form.Header>
        <Space wrap>
          <>
            <Button
              onClick={() => {
                setDatePickerVisible(true);
              }}
            >
              Modifier
            </Button>
            <DatePicker
              // onClick={() => {
              //   setDatePickerVisible(true);
              // }}
              value={new Date(intervention.timestamp)}
              visible={datePickerVisible}
              onClose={() => {
                setDatePickerVisible(false);
              }}
              precision="day"
              onConfirm={(val) => {
                val.setHours(new Date(intervention.timestamp).getHours());
                val.setMinutes(new Date(intervention.timestamp).getMinutes());
                setIntervention((intervention) => ({
                  ...intervention,
                  timestamp: val.toISOString(),
                }));
                setDatePickerVisible(false);
              }}
              confirmText="Ok"
              cancelText="Annuler"
              // renderLabel={labelRenderer}
            >
              {(value) => value?.toLocaleString()}
            </DatePicker>
          </>
        </Space>

        <Form.Header>Le véhicule :</Form.Header>

        {/* ===== Hero PLAQUE ===== */}
        <div
          style={{
            padding: '14px',
            background: 'var(--bg-card)',
            borderBottom: '1px solid var(--border-subtle)',
          }}
        >
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'baseline',
              marginBottom: 8,
            }}
          >
            <div
              style={{
                fontSize: 10,
                fontWeight: 800,
                letterSpacing: '0.14em',
                textTransform: 'uppercase',
                color: 'var(--text-muted)',
              }}
            >
              ▸ Plaque
            </div>
          </div>
          <div
            className="plate-hero-input"
            style={{
              background:
                'linear-gradient(180deg, #FFFFFF, #F0F0F0)',
              borderRadius: 10,
              padding: '10px 14px',
              boxShadow:
                '0 4px 14px rgba(0,0,0,0.35), inset 0 0 0 2px rgba(0,0,0,0.08)',
              marginBottom: 10,
            }}
          >
            <Form.Item name="immatriculation" noStyle>
              <Input
                placeholder="— — — — — — —"
                autoCapitalize="characters"
                autoCorrect="false"
                style={
                  {
                    '--font-size': '26px',
                    '--text-align': 'center',
                    '--color': '#0B1A2F',
                    '--placeholder-color': 'rgba(11,26,47,0.25)',
                    fontWeight: 900,
                    letterSpacing: '0.16em',
                    fontFamily:
                      '"SF Mono", "Menlo", "Consolas", monospace',
                  } as React.CSSProperties
                }
              />
            </Form.Item>
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <button
              type="button"
              onClick={() => {
                setScanCameraError(null);
                setScanKind('immat');
              }}
              style={{
                width: '100%',
                appearance: 'none',
                background:
                  'linear-gradient(135deg, rgba(49,220,200,0.16), rgba(49,220,200,0.04))',
                border: '1px solid rgba(49,220,200,0.45)',
                borderRadius: 12,
                padding: '14px 8px',
                color: 'var(--text-primary)',
                fontFamily: 'inherit',
                fontSize: 13.5,
                fontWeight: 800,
                cursor: 'pointer',
                WebkitTapHighlightColor: 'transparent',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 8,
              }}
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="var(--teal)" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z" />
                <circle cx="12" cy="13" r="4" />
              </svg>
              Scanner
            </button>
          </div>
        </div>

        {/* ===== Hero N° PARC ===== */}
        <div
          style={{
            padding: '14px',
            background: 'var(--bg-card)',
            borderBottom: '1px solid var(--border-subtle)',
          }}
        >
          <div
            style={{
              fontSize: 10,
              fontWeight: 800,
              letterSpacing: '0.14em',
              textTransform: 'uppercase',
              color: 'var(--text-muted)',
              marginBottom: 8,
            }}
          >
            ▸ N° de parc
          </div>
          <div
            style={{
              background: 'var(--bg-card)',
              border: '1px solid var(--border-strong)',
              borderRadius: 10,
              padding: '8px 14px',
              marginBottom: 10,
            }}
          >
            <Form.Item name="parc_number" noStyle>
              <Input
                placeholder="—"
                autoCapitalize="characters"
                autoCorrect="false"
                style={
                  {
                    '--font-size': '22px',
                    '--text-align': 'center',
                    '--color': '#fff',
                    '--placeholder-color': 'rgba(248,250,251,0.25)',
                    fontWeight: 800,
                    letterSpacing: '0.1em',
                  } as React.CSSProperties
                }
              />
            </Form.Item>
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <button
              type="button"
              onClick={() => {
                setScanCameraError(null);
                setScanKind('parc');
              }}
              style={{
                width: '100%',
                appearance: 'none',
                background:
                  'linear-gradient(135deg, rgba(49,220,200,0.12), rgba(49,220,200,0.03))',
                border: '1px solid rgba(49,220,200,0.4)',
                borderRadius: 12,
                padding: '12px 8px',
                color: 'var(--text-primary)',
                fontFamily: 'inherit',
                fontSize: 13,
                fontWeight: 700,
                cursor: 'pointer',
                WebkitTapHighlightColor: 'transparent',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 8,
              }}
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="var(--teal)" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z" />
                <circle cx="12" cy="13" r="4" />
              </svg>
              Scanner
            </button>
          </div>
        </div>
        {warnings.identifier && <NoticeBar content="Entrer une immatriculation ou un numéro de parc" color="alert" />}

        {client.has_additional_vehicles && (
          <>
            <Form.Header>Véhicule supplémentaire :</Form.Header>
            <Form.Item name="immatriculation_bis" label="Immatr. bis">
              <Input placeholder="..." autoCapitalize="characters" autoCorrect="false" />
            </Form.Item>
            <Form.Item name="parc_number_bis" label="N° Parc bis">
              <Input placeholder="..." autoCapitalize="characters" autoCorrect="false" />
            </Form.Item>
          </>
        )}

        <Form.Header />

        <Form.Item name="driver" label="Conducteur">
          <Input placeholder="..." autoCapitalize="characters" autoCorrect="false" />
        </Form.Item>
        <Form.Header />

        <Form.Item name="vehicle_type" required>
          <Selector<string>
            onChange={(vehicle_types) => handleVehicleTypeChange(vehicle_types)}
            columns={Math.min(costs.length, 3)}
            options={costs.map((c) => {
              return { label: c.title, value: c.title };
            })}
          />
        </Form.Item>
        {warnings.vehicleType && <NoticeBar content="Choisissez un type de vehicule" color="alert" />}

        <Form.Header>Type de prestation</Form.Header>
        <Form.Item name="prestations" required>
          {intervention.prestation_costs ? (
            <Selector<PrestationEnum>
              key={intervention.prestation_costs.title}
              onChange={handlePrestationsChange}
              multiple={true}
              columns={Math.min(choices.length, 3)}
              options={choices}
            />
          ) : (
            <Empty description="choisir un type de vehicule" />
          )}
        </Form.Item>
        {warnings.prestations && <NoticeBar content="Choisissez au moins une prestation" color="alert" />}

        <Form.Header>
          Commentaire <Switch checked={showComment} onChange={(checked) => setShowComment(checked)} />
        </Form.Header>
        {showComment && (
          <Form.Item name="comment">
            <TextArea placeholder="..." />
          </Form.Item>
        )}
      </Form>
      {popinVisible && intervention.prestation_costs && intervention.client_id && (
        <PopupValidation intervention={intervention as InterventionJson} onMaskClick={() => setPopinVisible(false)} returnPath={returnPath} />
      )}

      {/* ===== Modal scanner ===== */}
      <Popup
        visible={scanKind !== null}
        onMaskClick={() => !scanBusy && setScanKind(null)}
        bodyStyle={{
          background: 'var(--navy)',
          height: '85vh',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <div
          style={{
            padding: '14px 16px',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            borderBottom: '1px solid var(--border-subtle)',
          }}
        >
          <div style={{ color: 'var(--text-primary)', fontWeight: 800, fontSize: 15 }}>
            📷 Scanner {scanKind === 'parc' ? 'le n° de parc' : 'la plaque'}
          </div>
          <button
            type="button"
            onClick={() => !scanBusy && setScanKind(null)}
            style={{
              appearance: 'none',
              background: 'transparent',
              border: 'none',
              color: 'var(--text-secondary)',
              fontSize: 22,
              cursor: 'pointer',
              padding: 4,
            }}
          >
            ×
          </button>
        </div>
        <div
          style={{
            flex: 1,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            background: '#000',
            overflow: 'hidden',
          }}
        >
          {scanKind !== null && !scanCameraError && (
            <Webcam
              ref={webcamRef}
              audio={false}
              screenshotFormat="image/jpeg"
              videoConstraints={{ facingMode: 'environment' }}
              style={{ width: '100%', height: '100%', objectFit: 'cover' }}
              onUserMedia={() => {
                setScanCameraError(null);
                faroLogger.info('intervention_scan_camera_ok');
              }}
              onUserMediaError={(err: string | DOMException) => {
                const msg = err instanceof Error ? err.message : String(err);
                setScanCameraError(msg);
                faroLogger.error('intervention_scan_camera_error', { error: msg });
              }}
            />
          )}
          {scanCameraError && (
            <div
              style={{
                padding: '24px 22px',
                color: '#fff',
                background: '#000',
                width: '100%',
                textAlign: 'center',
                display: 'flex',
                flexDirection: 'column',
                gap: 14,
                alignItems: 'center',
              }}
            >
              <div style={{ fontSize: 48, lineHeight: 1 }}>📵</div>
              <div style={{ fontSize: 16, fontWeight: 800 }}>Caméra indisponible</div>
              <div style={{ fontSize: 13.5, lineHeight: 1.5, color: 'rgba(255,255,255,0.85)', maxWidth: 320 }}>
                L&rsquo;application n&rsquo;a pas accès à la caméra.
                <br />
                <br />
                <b style={{ color: '#31DCC8' }}>Sur iPhone :</b>
                <br />
                Réglages → Camion Net (ou Safari) → Caméra → <b>Autoriser</b>
                <br />
                puis fermer et rouvrir l&rsquo;app.
              </div>
              <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.45)', marginTop: 4 }}>
                Détail : {scanCameraError}
              </div>
            </div>
          )}
        </div>
        <div
          style={{
            padding: '14px 16px 18px',
            display: 'flex',
            justifyContent: 'center',
            background: 'var(--navy)',
          }}
        >
          <button
            type="button"
            disabled={scanBusy}
            onClick={handleScanCapture}
            style={{
              width: 72,
              height: 72,
              borderRadius: '50%',
              background: scanBusy
                ? 'rgba(49,220,200,0.4)'
                : 'var(--teal)',
              border: '4px solid rgba(255,255,255,0.85)',
              color: 'var(--navy)',
              fontSize: 24,
              fontWeight: 900,
              cursor: scanBusy ? 'wait' : 'pointer',
              boxShadow: '0 8px 24px rgba(49,220,200,0.35)',
            }}
          >
            {scanBusy ? '…' : '●'}
          </button>
        </div>
      </Popup>

    </div>
  );
};

export default NewIntervention;
